import { useEffect, useState } from 'react';
import { FiUpload } from 'react-icons/fi';
import { Box, Button, Flex, Text, useToast } from '@chakra-ui/react';
import { UPLOAD_FILE_ALLOWED_FORMATS } from 'constants/constants';
import useUploadFile from 'hooks/useUploadFile';
import { FILE_EXTENSION_REGEX } from 'modules/kyc/constants/constants';
import { ENTITY_OWNER_DOCUMENT_UPLOAD_ID } from 'modules/merchantinfo/constants/constants';
import { showToast } from 'utils/notifications';

function FileUploader({ acceptedFormats, numDocuments, maxDocuments, selectedFiles, setSelectedFiles, id }) {
  const toast = useToast();
  const [error, setError] = useState('');

  const { uploadFile, isLoading } = useUploadFile({
    id: id,
    acceptedFormats,
    setSelectedFiles,
    setError,
  });

  const handleUploadButtonClick = () => {
    document.getElementById(ENTITY_OWNER_DOCUMENT_UPLOAD_ID)?.click();
  };

  const handleDocumentChange = (event) => {
    const files = event.target.files;

    if (!files || files?.length === 0) return;

    setError('');

    if (files?.length + selectedFiles?.length > maxDocuments) {
      setError(`* You can upload up to ${maxDocuments} documents.`);

      return;
    }

    Array.from(files).forEach((file) =>
      uploadFile(file).then((res) => {
        setSelectedFiles((prevFiles) => [...prevFiles, res]);
      })
    );
  };

  const validFileExtensions = acceptedFormats
    ?.split(', ')
    ?.map((ext) => ext.replace(FILE_EXTENSION_REGEX, '').toUpperCase())
    .join(', ');

  useEffect(() => {
    if (selectedFiles?.length === numDocuments) {
      showToast(toast, 'Documents uploaded successfully', 'All documents uploaded.', 'success');
    } else if (selectedFiles?.length > 0 && selectedFiles?.length < numDocuments) {
      showToast(toast, 'Incomplete Upload', 'Some documents are missing.', 'warning');
    }
  }, [selectedFiles, numDocuments]);

  return (
    <Box>
      <Flex
        direction='column'
        align='center'
        gap={2}
        p={10}
        borderWidth='1px'
        borderRadius='6px'
        backgroundColor='rgba(240,248,255,0.5)'
      >
        <Box width='100%'>
          {selectedFiles?.map((file, index) => (
            <Text key={index} isTruncated color='gray'>
              📄 {file?.fileName}
            </Text>
          ))}
        </Box>
        <Button
          isDisabled={selectedFiles?.length >= numDocuments}
          leftIcon={<FiUpload />}
          width='fit-content'
          borderRadius='20px'
          borderWidth='2px'
          onClick={handleUploadButtonClick}
          isLoading={isLoading}
        >
          Upload Document
        </Button>
        <input
          id={ENTITY_OWNER_DOCUMENT_UPLOAD_ID}
          type='file'
          accept={UPLOAD_FILE_ALLOWED_FORMATS}
          multiple
          style={{ display: 'none' }}
          onChange={handleDocumentChange}
        />
      </Flex>
      {error && (
        <Text color='red.500' fontSize='sm' mt={2}>
          {error}
        </Text>
      )}
      <Text fontSize='xs' color='gray.600' mt={2}>
        {`Supported files: ${validFileExtensions}, max 2MB.`}
      </Text>
    </Box>
  );
}

export default FileUploader;
